import { type Content, type ContentHref, type WebPage, isContent } from '@generalTypes/apiTypes';
import { getContentNodeId } from '@newStore/documentApi/documentApiHelpers';
import { wrapNodeTypeLabel } from '@newStore/documentUI/transformProposal/proposalHelpers';
import { stripHtml } from '@newStore/genericHelpers';
import {
  type EditComponentProperty,
  type FilterableEditComponent,
  type NodeTypeConfig,
  RequiredType,
  type WebTabComponent,
  isEditAttachmentsComponent,
  isWebTabComponent,
} from '@nodeTypeConfig/configTypes';
import { get } from 'lodash';
import { createError } from './createError';
import {
  type ValidationErrorWithNode,
  type ValidationInfo,
  type ValidationMapIndex,
  type ValidationPending,
  type ValidationResult,
  type ValidationStatus,
  isValidationErrorOrWarning,
} from './validationTypes';

const getPropertyValue = (content: Content | WebPage, property: string) => {
  return get(content, property);
};

export const isContentEmpty = (
  content: Content | WebPage | undefined,
  prop: EditComponentProperty,
  component: FilterableEditComponent | WebTabComponent,
): boolean => {
  if (!content) {
    return true;
  }

  if (
    !isWebTabComponent(component) &&
    isEditAttachmentsComponent(component) &&
    isContent(content)
  ) {
    return (
      !content.attachments || !content.attachments.some((a) => a.type === component.options.type)
    );
  }
  const value = getPropertyValue(content, prop);
  if (value === null || value === undefined) {
    return true;
  }
  if (Array.isArray(value) && !value.length) {
    return true;
  }
  if (typeof value === 'string' && (value.length === 0 || stripHtml(value).trim().length === 0)) {
    return true;
  }
  return false;
};

export const isContentTooLong = (
  content: Content | WebPage | undefined,
  prop: string,
  maxLength: number,
): boolean => {
  return content?.[prop] && stripHtml(content[prop]).trim().length > maxLength;
};

/* We want just the first letter in lowercase unless the second letter is uppercase (eg: WWW) */
export const getNodeTypeLabel = (name: string) => {
  const nodeTypeLabel =
    name[1] === name[1].toLocaleUpperCase()
      ? name
      : name[0].toLocaleLowerCase() + name.substring(1);
  return wrapNodeTypeLabel`${nodeTypeLabel}`;
};

export const getNodeTypeLabelFromConfig = (nodeTypeConfig: NodeTypeConfig) =>
  getNodeTypeLabel(nodeTypeConfig.information.single);

export const getValidationStatus = (
  validationResults: Array<ValidationResult>,
  ignoreUnknown: boolean,
): ValidationStatus => {
  if (!validationResults.length) {
    return 'VALID';
  }
  return validationResults.reduce((status: ValidationStatus, res) => {
    if (isValidationErrorOrWarning(res) && res.type === 'ERROR') {
      return 'INVALID';
    }
    if (!ignoreUnknown && res === null && status !== 'INVALID') {
      return 'UNKNOWN';
    }
    if (
      status !== 'INVALID' &&
      status !== 'UNKNOWN' &&
      isValidationErrorOrWarning(res) &&
      res.type === 'WARNING'
    ) {
      return 'VALID_WITH_WARNINGS';
    }
    return status;
  }, 'VALID' as ValidationStatus);
};

export const getHighestValidationStatus = (statuses: Array<ValidationInfo>): ValidationStatus => {
  if (statuses.some((status) => status.status === 'INVALID')) {
    return 'INVALID';
  }
  if (statuses.some((status) => status.status === 'UNKNOWN')) {
    return 'UNKNOWN';
  }
  if (statuses.some((status) => status.status === 'VALID_WITH_WARNINGS')) {
    return 'VALID_WITH_WARNINGS';
  }
  return 'VALID';
};

export const createChildError = (
  parentHref: ContentHref | undefined,
  childHref: ContentHref,
): ValidationErrorWithNode => {
  const error: ValidationErrorWithNode = {
    ...createError(
      'childError',
      'childError',
      'Er zijn foutmeldingen voor de <strong>onderliggende inhoud</strong>.',
      undefined,
      RequiredType.ERROR,
    ),
    node: {
      href: childHref,
      parentHref,
      nodeId: getContentNodeId({ childHref, parentHref }),
    },
  };
  return error;
};

export const createChildWarning = (
  parentHref: ContentHref | undefined,
  childHref: ContentHref,
): ValidationErrorWithNode => {
  const warning: ValidationErrorWithNode = {
    ...createError(
      'childWarning',
      'childWarning',
      'Er zijn waarschuwingen voor de <strong>onderliggende inhoud</strong>',
      undefined,
      RequiredType.WARNING,
    ),
    node: {
      href: childHref,
      parentHref,
      nodeId: getContentNodeId({ childHref, parentHref }),
    },
  };
  return warning;
};

export const VALIDATION_PENDING: ValidationPending = { pending: true };

// export const getValidationInfo = (
//   validationResultsForNode: Array<ValidationResult>,
//   valResultsForDescendants: Array<ValidationResult>
// ): ValidationInfo => {
//   const nodeValidationErrors = validationResultsForNode.filter(isValidationErrorOrWarning);
//   const descValidationErrors = valResultsForDescendants.filter(isValidationErrorOrWarning);
//   if (descValidationErrors.some((e) => e.type === 'ERROR')) {
//     nodeValidationErrors.push(childError);
//   }
//   if (descValidationErrors.some((e) => e.type === 'WARNING')) {
//     nodeValidationErrors.push(childWarning);
//   }

//   return {
//     status: getValidationStatus([...validationResultsForNode, ...valResultsForDescendants], true),
//     validationErrors: nodeValidationErrors,
//   };
// };

export const getParentChildIndex = ({
  parentHref,
  childHref,
}: {
  parentHref: string | undefined;
  childHref: string;
}): ValidationMapIndex => {
  return `${parentHref}-${childHref}` as ValidationMapIndex;
};
